import axios from 'axios'
import toast from 'react-hot-toast';

// Automatische Erkennung der API-URL basierend auf der Umgebung
const getApiBaseUrl = () => {
  if (import.meta.env.DEV) {
    // Development: Verwende Vite-Proxy für CORS-freie Anfragen
    return '/api';
  } else {
    // Production: Verwende die aktuelle Domain
    const backendUrl = window.location.origin;
    return `${backendUrl}/api`;
  }
};

// API-Client mit automatischer Umgebungserkennung
const apiBaseUrl = getApiBaseUrl();
console.log('🌐 API Base URL:', apiBaseUrl);

export const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 Minuten Timeout für LLM-Verarbeitung (Curricula Import)
  withCredentials: true, // Send cookies with requests
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Bei FormData keinen Content-Type setzen – Browser setzt multipart/form-data inkl. Boundary
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    // Cookies werden automatisch vom Browser gesendet (withCredentials: true)
    // Kein manuelles Token-Handling mehr erforderlich

    // Log API-Aufrufe NUR im Development-Modus
    if (import.meta.env.DEV && import.meta.env.MODE === 'development') {
      console.log(`🌐 API Request: ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

let lastThrottleStatus = 'NORMAL'; // Für User-Throttle-Statuswechsel

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log API-Antworten NUR im Development-Modus
    if (import.meta.env.DEV && import.meta.env.MODE === 'development') {
      console.log(`✅ API Response: ${response.status} ${response.config.url}`)
    }
    return response
  },
  (error) => {
    // DEV: Throttle-Daten debuggen
    if (import.meta.env.DEV && error.response?.data?.throttle) {
      console.log('🪝 [Throttle-Debug] Fehlerobjekt throttle:', error.response.data.throttle);
    }

    // Versuche beide Feldnamen ("level" und "status") für User-Throttling
    const throttleStatus = error?.response?.data?.throttle?.level || error?.response?.data?.throttle?.status || 'NORMAL';
    if (throttleStatus !== lastThrottleStatus) {
      if (["WARNING", "CRITICAL", "FRIENDLY_LIMIT"].includes(throttleStatus)) {
        toast("Hinweis: Du nutzt Lernrakete aktuell sehr intensiv. Um einen reibungslosen Betrieb für alle zu gewährleisten, wird deine Geschwindigkeit kurzzeitig etwas angepasst. Vielen Dank für dein Engagement!", { icon: "🚀", duration: 9000 });
      } else if (["WARNING", "CRITICAL", "FRIENDLY_LIMIT"].includes(lastThrottleStatus) && throttleStatus === "NORMAL") {
        toast.success("Volle Power: Lernrakete steht dir ab sofort wieder im gewohnten Tempo zur Verfügung.", { icon: "⚡", duration: 7000 });
      }
      lastThrottleStatus = throttleStatus;
    }
    if (error.response?.status === 401) {
      // Cookie wurde vom Server entfernt oder ist abgelaufen
      // Leite zum Login weiter - ABER NUR wenn nicht bereits auf Login-Seite
      // UND NUR wenn es sich NICHT um eine öffentliche Route handelt
      const pathname = window.location.pathname;
      const reqUrl = String(error.config?.url ?? '');
      // Proxy-/Upstream-Fehler (z. B. WestAI-Key) dürfen nicht wie Session-401 behandelt werden
      const isUpstreamStyleRoute =
        reqUrl.includes('/stt') || reqUrl.includes('/tts') || reqUrl.includes('/assistant');
      const isPublicRoute = pathname.startsWith('/elternbrief/') || 
                           (pathname.startsWith('/appointments/') && 
                            !pathname.startsWith('/appointments/student')) ||
                           pathname.startsWith('/display');
      
      // Für öffentliche Routen keine Weiterleitung zum Login
      if (!isUpstreamStyleRoute && !isPublicRoute && window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }
    // Log API-Fehler NUR im Development-Modus
    if (import.meta.env.DEV && import.meta.env.MODE === 'development') {
      console.error(`❌ API Error: ${error.response?.status} ${error.config?.url}`, error.response?.data)
    }
    return Promise.reject(error)
  }
)

// Hilfsfunktionen für häufige API-Operationen
export const apiClient = {
  // GET-Request
  get: (endpoint: string, config = {}) => api.get(endpoint, config),
  
  // POST-Request
  post: (endpoint: string, data = {}, config = {}) => api.post(endpoint, data, config),
  
  // PUT-Request
  put: (endpoint: string, data = {}, config = {}) => api.put(endpoint, data, config),
  
  // DELETE-Request
  delete: (endpoint: string, config = {}) => api.delete(endpoint, config),
  
  // PATCH-Request
  patch: (endpoint: string, data = {}, config = {}) => api.patch(endpoint, data, config),
  
  // Aktuelle API-URL (für Debugging)
  getBaseUrl: () => getApiBaseUrl(),
  
  // Umgebung anzeigen
  getEnvironment: () => import.meta.env.DEV ? 'development' : 'production'
}

export { studentAssessmentsApi } from './studentAssessmentsApi'
export type {
  StudentAssessmentAlertLevel,
  StudentAssessmentAutomationConfig,
  StudentAssessmentClassRunResponse,
  StudentAssessmentConfigPayload,
  StudentAssessmentRun,
  StudentAssessmentScheduleInterval,
  StudentAssessmentStudentRunResponse
} from './studentAssessmentsApi'

// Export der ursprünglichen API-Instanz für Abwärtskompatibilität
export default api
